(() => {
  'use strict';

  const STORAGE_KEY = 'wishlist';
  const COUNT_KEY = 'user_wishlist_cnt';

  const BUTTON_SELECTOR =
    '.wishlist[data-handle]';

  const COUNTER_SELECTOR =
    '.wishlist-counter';

  /*
   * Used as a temporary fallback when localStorage
   * is unavailable or blocked by the browser.
   */
  let memoryWishlist = [];


  /* =========================================================
     NORMALIZE WISHLIST DATA
  ========================================================= */

  const normalizeHandles = (value) => {
    if (!Array.isArray(value)) {
      return [];
    }

    return [
      ...new Set(
        value
          .filter((handle) => {
            return (
              typeof handle === 'string' &&
              handle.trim() !== ''
            );
          })
          .map((handle) => handle.trim())
      )
    ];
  };


  /* =========================================================
     READ WISHLIST
  ========================================================= */

  const readWishlist = () => {
    try {
      const storedValue =
        localStorage.getItem(STORAGE_KEY);

      const parsedValue =
        storedValue
          ? JSON.parse(storedValue)
          : [];

      memoryWishlist =
        normalizeHandles(parsedValue);

      return [...memoryWishlist];
    } catch (error) {
      return [...memoryWishlist];
    }
  };


  /* =========================================================
     SAVE WISHLIST
  ========================================================= */

  const writeWishlist = (handles) => {
    const cleanHandles =
      normalizeHandles(handles);

    memoryWishlist = cleanHandles;

    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(cleanHandles)
      );

      localStorage.setItem(
        COUNT_KEY,
        String(cleanHandles.length)
      );
    } catch (error) {
      console.warn(
        '[Zyvira Wishlist] Browser storage is unavailable.',
        error
      );
    }

    return cleanHandles;
  };


  /* =========================================================
     PRODUCT NAME FOR ACCESSIBILITY
  ========================================================= */

  const getProductName = (button) => {
    return (
      button.dataset.productTitle?.trim() ||
      button.getAttribute('data-title')?.trim() ||
      'this product'
    );
  };


  /* =========================================================
     UPDATE WISHLIST COUNTERS
  ========================================================= */

  const updateCounters = (count) => {
    document
      .querySelectorAll(COUNTER_SELECTOR)
      .forEach((counter) => {
        counter.textContent =
          String(count);
      });

    try {
      localStorage.setItem(
        COUNT_KEY,
        String(count)
      );
    } catch (error) {
      /*
       * The visible counter still updates even when
       * localStorage is unavailable.
       */
    }
  };


  /* =========================================================
     UPDATE ONE WISHLIST BUTTON
  ========================================================= */

  const updateButtonState = (
    button,
    isActive
  ) => {
    const productName =
      getProductName(button);

    button.classList.toggle(
      'active-wishlist',
      isActive
    );

    button.classList.toggle(
      'wishlist-custom-action',
      !isActive
    );

    button.setAttribute(
      'aria-pressed',
      String(isActive)
    );

    button.setAttribute(
      'aria-label',
      isActive
        ? `Remove ${productName} from wishlist`
        : `Add ${productName} to wishlist`
    );

    button.setAttribute(
      'title',
      isActive
        ? 'Remove from Wishlist'
        : 'Add to Wishlist'
    );

    const icon =
      button.querySelector('i');

    if (icon) {
      icon.classList.toggle(
        'ri-heart-line',
        !isActive
      );

      icon.classList.toggle(
        'ri-heart-fill',
        isActive
      );

      icon.setAttribute(
        'aria-hidden',
        'true'
      );
    }

    /*
     * Some product-page wishlist buttons contain
     * visible text. Preserve their original label.
     */
    const visibleText =
      button.querySelector('.wishlist-text');

    if (visibleText) {
      if (
        !visibleText.dataset
          .defaultWishlistText
      ) {
        visibleText.dataset
          .defaultWishlistText =
            visibleText.textContent.trim() ||
            'Wishlist';
      }

      visibleText.textContent =
        isActive
          ? 'In Wishlist'
          : visibleText.dataset
              .defaultWishlistText;
    }
  };


  /* =========================================================
     SYNCHRONIZE ALL WISHLIST BUTTONS
  ========================================================= */

  const syncWishlistButtons = (
    root = document
  ) => {
    const wishlist =
      readWishlist();

    const wishlistSet =
      new Set(wishlist);

    const buttons = [];

    if (
      root instanceof Element &&
      root.matches(BUTTON_SELECTOR)
    ) {
      buttons.push(root);
    }

    if (root.querySelectorAll) {
      buttons.push(
        ...root.querySelectorAll(
          BUTTON_SELECTOR
        )
      );
    }

    buttons.forEach((button) => {
      const handle =
        button.dataset.handle?.trim();

      updateButtonState(
        button,
        Boolean(
          handle &&
          wishlistSet.has(handle)
        )
      );
    });

    updateCounters(
      wishlist.length
    );
  };


  /* =========================================================
     ADD OR REMOVE PRODUCT
  ========================================================= */

  const toggleWishlistProduct = (
    button
  ) => {
    const handle =
      button.dataset.handle?.trim();

    if (!handle) {
      return;
    }

    let wishlist =
      readWishlist();

    const alreadySaved =
      wishlist.includes(handle);

    if (alreadySaved) {
      wishlist =
        wishlist.filter(
          (savedHandle) =>
            savedHandle !== handle
        );
    } else {
      wishlist.push(handle);
    }

    wishlist =
      writeWishlist(wishlist);

    /*
     * Synchronizes every copy of this product:
     * homepage, collection, cart drawer and
     * wishlist page.
     */
    syncWishlistButtons(document);

    document.dispatchEvent(
      new CustomEvent(
        'wishlist:updated',
        {
          detail: {
            handle,
            active: !alreadySaved,
            count: wishlist.length
          }
        }
      )
    );
  };


  /* =========================================================
     DELEGATED CLICK HANDLER
  ========================================================= */

  const handleWishlistClick = (
    event
  ) => {
    const button =
      event.target.closest?.(
        BUTTON_SELECTOR
      );

    if (!button) {
      return;
    }

    event.preventDefault();

    toggleWishlistProduct(
      button
    );
  };


  /* =========================================================
     INITIALIZATION
  ========================================================= */

  const initializeWishlist = () => {
    syncWishlistButtons(document);

    document.addEventListener(
      'click',
      handleWishlistClick
    );

    /*
     * Reinitialize buttons loaded through the
     * Shopify Theme Editor.
     */
    document.addEventListener(
      'shopify:section:load',
      (event) => {
        syncWishlistButtons(
          event.target
        );
      }
    );

    /*
     * The cart drawer is replaced through AJAX.
     * This observer activates newly inserted
     * wishlist buttons automatically.
     */
    const observer =
      new MutationObserver(
        (mutations) => {
          mutations.forEach(
            (mutation) => {
              mutation.addedNodes.forEach(
                (node) => {
                  if (
                    node.nodeType !==
                    Node.ELEMENT_NODE
                  ) {
                    return;
                  }

                  syncWishlistButtons(
                    node
                  );
                }
              );
            }
          );
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

    /*
     * Synchronize wishlist state between
     * multiple open browser tabs.
     */
    window.addEventListener(
      'storage',
      (event) => {
        if (
          event.key === STORAGE_KEY ||
          event.key === COUNT_KEY
        ) {
          syncWishlistButtons(
            document
          );
        }
      }
    );
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeWishlist,
      { once: true }
    );
  } else {
    initializeWishlist();
  }
})();